import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/pensionCalc';
import { taxMonthToName } from '../utils/constants';

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};


// ─────────────────────────────────────────────────────────────────────────
// Submission History (Phase 13) — skim-able list of past submissions
// ─────────────────────────────────────────────────────────────────────────

const TYPE_PILL = {
  new_starter:        { tone: 'emerald', singular: 'starter',    plural: 'starters' },
  leaver:             { tone: 'rose',    singular: 'leaver',     plural: 'leavers' },
  salary:             { tone: 'brand',   singular: 'salary',     plural: 'salary' },
  contractual_change: { tone: 'brand',   singular: 'contractual',plural: 'contractual' },
  pension:            { tone: 'violet',  singular: 'pension',    plural: 'pension' },
  parental_leave:     { tone: 'amber',   singular: 'parental',   plural: 'parental' },
  sick_leave:         { tone: 'amber',   singular: 'sick',       plural: 'sick' },
  addition:           { tone: 'emerald', singular: 'addition',   plural: 'additions' },
  deduction:          { tone: 'amber',   singular: 'deduction',  plural: 'deductions' },
  bank_details:       { tone: 'gray',    singular: 'bank',       plural: 'bank' },
  address_change:     { tone: 'gray',    singular: 'address',    plural: 'address' },
  personal_details:   { tone: 'gray',    singular: 'personal',   plural: 'personal' },
  commission:         { tone: 'sky',     singular: 'commission', plural: 'commission' },
  hourly:             { tone: 'amber',   singular: 'hourly',     plural: 'hourly' },
};

const TONE_PILL_CLS = {
  emerald: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  rose:    'bg-rose-100 text-rose-800 ring-rose-200',
  brand:   'bg-brand-100 text-brand-700 ring-brand-200',
  violet:  'bg-violet-100 text-violet-800 ring-violet-200',
  amber:   'bg-amber-100 text-amber-800 ring-amber-200',
  sky:     'bg-sky-100 text-sky-800 ring-sky-200',
  gray:    'bg-gray-100 text-gray-700 ring-gray-200',
};

function MiniCountPill({ count, type }) {
  const def = TYPE_PILL[type];
  if (!def || !count) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11.5px] font-semibold ring-1 ${TONE_PILL_CLS[def.tone] || TONE_PILL_CLS.gray}`}>
      {count} {count === 1 ? def.singular : def.plural}
    </span>
  );
}

function readSubmissionData(s) {
  const data = typeof s.changes_data === 'string' ? JSON.parse(s.changes_data) : s.changes_data;
  const changes = data?.changes || [];
  const commissions = (data?.commissions || []).filter(c => c.commission > 0);
  const hourlyEntries = (data?.hourly_entries || []).filter(h => parseFloat(h.hours_this_period) > 0);
  return { data, changes, commissions, hourlyEntries };
}

function countsFor(s) {
  const { changes, commissions, hourlyEntries } = readSubmissionData(s);
  const c = {};
  changes.forEach(ch => { c[ch.type] = (c[ch.type] || 0) + 1; });
  if (commissions.length) c.commission = commissions.length;
  if (hourlyEntries.length) c.hourly = hourlyEntries.length;
  return c;
}

function SubmissionCard({ submission, isExpanded, onToggle, isActive }) {
  const { changes, commissions, hourlyEntries } = readSubmissionData(submission);
  const totalItems = changes.length + commissions.length + hourlyEntries.length;
  const isNoChanges = submission.submission_type === 'no_changes' || totalItems === 0;
  const periodLabel = submission.tax_month
    ? `${taxMonthToName(submission.tax_month)} ${submission.tax_year}`
    : 'Unknown period';
  const taxLabel = submission.tax_month
    ? `Tax M${submission.tax_month} ${submission.tax_year}/${(submission.tax_year + 1).toString().slice(-2)}`
    : '';
  const submittedDateShort = new Date(submission.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const submittedDateFull = new Date(submission.submitted_at).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const counts = countsFor(submission);
  const countEntries = Object.entries(counts).filter(([, n]) => n > 0);

  // Group changes by type for the expanded body
  const grouped = {};
  changes.forEach(c => { (grouped[c.type] = grouped[c.type] || []).push(c); });

  const typeLabels = {
    new_starter: 'New starters', leaver: 'Leavers', salary: 'Salary changes',
    pension: 'Pension changes', parental_leave: 'Parental leave', sick_leave: 'Sick leave',
    addition: 'Additions', deduction: 'Deductions', bank_details: 'Bank details',
    address_change: 'Address changes', contractual_change: 'Contractual changes',
    personal_details: 'Personal details',
  };

  const ringCls = isActive ? 'ring-brand-300' : 'ring-gray-200';

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl ring-1 ${ringCls} overflow-hidden`}>
      {/* Collapsed row */}
      <div
        className="grid items-center px-5 py-4 cursor-pointer hover:bg-gray-50/60 dark:hover:bg-gray-700/40 transition-colors"
        style={{ gridTemplateColumns: '54px minmax(0,1fr) minmax(0,260px) 130px 16px' }}
        onClick={onToggle}
      >
        <div>
          <span className={`grid place-items-center w-12 h-12 rounded-full ${isNoChanges ? 'bg-emerald-100 text-emerald-700' : 'bg-brand-100 text-brand-700'}`}>
            {isNoChanges ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            ) : (
              <span className="font-bold text-[15px]">{totalItems}</span>
            )}
          </span>
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="font-heading text-[16px] text-ink dark:text-gray-100 truncate">{periodLabel}</h4>
            {taxLabel && <span className="text-[12.5px] text-brand-700">· {taxLabel}</span>}
          </div>
          <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">Submitted {submittedDateShort}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {countEntries.length === 0
            ? <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11.5px] font-semibold ring-1 ${TONE_PILL_CLS.gray}`}>No changes</span>
            : countEntries.map(([type, n]) => <MiniCountPill key={type} type={type} count={n} />)
          }
        </div>
        <div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[12px] font-semibold ring-1 ${submission.processed ? TONE_PILL_CLS.emerald : TONE_PILL_CLS.amber}`}>
            {submission.processed ? 'Processed' : 'Awaiting processing'}
          </span>
        </div>
        <svg
          className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        ><polyline points="9 18 15 12 9 6" /></svg>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-gray-700 bg-gradient-to-b from-brand-50/30 to-white dark:from-brand-900/20 dark:to-gray-800 px-5 py-5 grid gap-5 grid-cols-1 md:grid-cols-2">
          {/* Left: changes + commission summary */}
          <div className="min-w-0">
            {Object.keys(grouped).length === 0 && commissions.length === 0 && hourlyEntries.length === 0 ? (
              <p className="text-[13px] text-gray-500 italic">Confirmed no changes for this period.</p>
            ) : (
              <>
                {Object.keys(grouped).length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2">Changes ({changes.length})</p>
                    <div className="space-y-1.5 mb-4">
                      {Object.entries(grouped).map(([type, items]) => items.map((item, idx) => {
                        const d = item.data || {};
                        const def = TYPE_PILL[type];
                        const tone = def?.tone || 'gray';
                        let summary = '';
                        if (type === 'salary') summary = d.new_annual_salary ? `New: ${formatCurrency(d.new_annual_salary)}/yr` : d.new_hourly_rate ? `New: ${formatCurrency(d.new_hourly_rate)}/hr` : '';
                        else if (type === 'leaver') summary = `Last day ${formatDate(d.leaving_date || d.leave_date) || '—'}`;
                        else if (type === 'new_starter') summary = `Start ${formatDate(d.start_date) || 'TBD'}`;
                        else if (type === 'addition') summary = `${d.addition_type_label || d.addition_type || ''}${d.amount ? ` · ${formatCurrency(d.amount)}` : ''}`;
                        else if (type === 'deduction') summary = `${d.deduction_type_label || d.deduction_type || ''}${d.amount ? ` · ${formatCurrency(d.amount)}` : ''}`;
                        else if (type === 'contractual_change') summary = `${d.change_type_label || d.change_type || ''}`;
                        else summary = typeLabels[type] || type;
                        return (
                          <div key={`${type}-${idx}`} className={`bg-white dark:bg-gray-900 rounded-lg ring-1 ${TONE_PILL_CLS[tone].split(' ').filter(c => c.startsWith('ring-')).join(' ')} px-3 py-2 flex items-center justify-between gap-3`}>
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-ink dark:text-gray-100 truncate">{item.employee_name}</p>
                              <p className="text-[11.5px] text-gray-500 dark:text-gray-400 mt-0.5">{typeLabels[type] || type}</p>
                            </div>
                            {summary && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11.5px] font-semibold ring-1 shrink-0 ${TONE_PILL_CLS[tone]}`}>{summary}</span>
                            )}
                          </div>
                        );
                      }))}
                    </div>
                  </>
                )}

                {commissions.length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2">Commission ({commissions.length})</p>
                    <div className="space-y-1 mb-2">
                      {commissions.map((c, idx) => (
                        <div key={idx} className="flex items-center justify-between text-[13px] px-3 py-1.5 bg-white dark:bg-gray-900 rounded-md ring-1 ring-sky-200 dark:ring-sky-800">
                          <span className="text-gray-800 dark:text-gray-200">{c.employee_name}</span>
                          <span className="text-sky-700 dark:text-sky-300 font-semibold tabular">{formatCurrency(c.commission)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {hourlyEntries.length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2 mt-3">Hourly hours ({hourlyEntries.length})</p>
                    <div className="space-y-1">
                      {hourlyEntries.map((h, idx) => {
                        const hours = parseFloat(h.hours_this_period) || 0;
                        const rate = parseFloat(h.hourly_rate) || 0;
                        const gross = h.gross !== undefined ? parseFloat(h.gross) : hours * rate;
                        return (
                          <div key={idx} className="flex items-center justify-between text-[13px] px-3 py-1.5 bg-white dark:bg-gray-900 rounded-md ring-1 ring-amber-200 dark:ring-amber-800">
                            <span className="text-gray-800 dark:text-gray-200">{h.employee_name}<span className="text-[11.5px] text-gray-500 dark:text-gray-400 ml-1.5">({formatCurrency(rate)}/hr)</span></span>
                            <span className="text-amber-800 dark:text-amber-300 font-semibold tabular">{hours.toFixed(2)} hrs · {formatCurrency(gross)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Right: submission receipt */}
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-2">Submission receipt</p>
            <div className="bg-white dark:bg-gray-900 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
              <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
                <span className="text-[12px] text-gray-500 dark:text-gray-400">Submitted by</span>
                <span className="text-[13px] text-gray-800 dark:text-gray-200 truncate">{submission.submitted_by_name || submission.submitted_by_email || 'You'}</span>
              </div>
              <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
                <span className="text-[12px] text-gray-500 dark:text-gray-400">At</span>
                <span className="text-[13px] text-gray-800 dark:text-gray-200 tabular">{submittedDateFull}</span>
              </div>
              {submission.pay_day_date && (
                <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
                  <span className="text-[12px] text-gray-500 dark:text-gray-400">Pay day</span>
                  <span className="text-[13px] text-gray-800 dark:text-gray-200 tabular">{formatDate(submission.pay_day_date)}</span>
                </div>
              )}
              <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
                <span className="text-[12px] text-gray-500 dark:text-gray-400">Total items</span>
                <span className="text-[14px] font-bold text-ink dark:text-gray-100 tabular">{totalItems}</span>
              </div>
              <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
                <span className="text-[12px] text-gray-500">Status</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11.5px] font-semibold ring-1 ${submission.processed ? TONE_PILL_CLS.emerald : TONE_PILL_CLS.amber}`}>
                  {submission.processed ? 'Processed' : 'Awaiting processing'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SubmissionHistoryPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    async function loadHistory() {
      try {
        setError('');
        const res = await api.get('/submissions');
        setSubmissions(res.data);
        // Auto-expand the most recent submission
        setExpandedId(res.data[0]?.id || null);
      } catch (err) {
        console.error('Failed to load submission history:', err);
        setError('Failed to load submission history. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadHistory();
  }, []);

  const stats = useMemo(() => {
    const total = submissions.length;
    const months = new Set(submissions.map(s => `${s.tax_year}-${s.tax_month}`)).size;
    // Aggregate counts across all submissions to find the leading category
    const totals = {};
    submissions.forEach(s => {
      const c = countsFor(s);
      Object.entries(c).forEach(([k, n]) => { totals[k] = (totals[k] || 0) + n; });
    });
    let topType = null; let topCount = 0;
    Object.entries(totals).forEach(([k, n]) => { if (n > topCount) { topType = k; topCount = n; } });
    return { total, months, topType, topCount };
  }, [submissions]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading submission history…</div>;
  if (error) {
    return (
      <div className="max-w-[1100px] mx-auto px-8 py-7">
        <p className="text-rose-600 font-medium">{error}</p>
        <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700">Retry</button>
      </div>
    );
  }

  const topDef = stats.topType ? TYPE_PILL[stats.topType] : null;
  const topLabel = topDef ? (stats.topCount === 1 ? topDef.singular : topDef.plural) : null;

  return (
    <div className="max-w-[1100px] mx-auto px-8 py-7">
      <header className="mb-6">
        <h1 className="font-heading text-[28px] leading-none text-ink dark:text-gray-100">Submission history</h1>
        <p className="text-gray-500 dark:text-gray-400 text-[14.5px] mt-2 max-w-[640px]">
          Everything you've sent through. Click any month to see the breakdown of changes.
        </p>
      </header>

      {submissions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500">No submissions yet.</p>
          <p className="text-[13px] text-gray-400 mt-1">Your submission history will appear here after your first submission.</p>
        </div>
      ) : (
        <>
          {/* Quick stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-5 py-4">
              <p className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">Total submissions</p>
              <p className="font-heading text-[24px] tabular leading-none mt-1.5 text-brand-700">{stats.total}</p>
              <p className="text-[12px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />over {stats.months} period{stats.months === 1 ? '' : 's'}
              </p>
            </div>
            {topDef && (
              <div className="bg-white dark:bg-gray-800 rounded-xl ring-1 ring-gray-200 dark:ring-gray-700 px-5 py-4">
                <p className="text-[11.5px] uppercase tracking-wider text-gray-500 font-semibold">Most changed</p>
                <p className={`font-heading text-[24px] leading-none mt-1.5 capitalize ${topDef.tone === 'emerald' ? 'text-emerald-700' : topDef.tone === 'rose' ? 'text-rose-700' : topDef.tone === 'sky' ? 'text-sky-700' : topDef.tone === 'amber' ? 'text-amber-700' : topDef.tone === 'violet' ? 'text-violet-700' : 'text-brand-700'}`}>
                  {topDef.plural}
                </p>
                <p className="text-[12px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${topDef.tone === 'emerald' ? 'bg-emerald-500' : topDef.tone === 'rose' ? 'bg-rose-500' : topDef.tone === 'sky' ? 'bg-sky-500' : topDef.tone === 'amber' ? 'bg-amber-500' : topDef.tone === 'violet' ? 'bg-violet-500' : 'bg-brand-500'}`} />
                  {stats.topCount} {topLabel} across the period
                </p>
              </div>
            )}
          </div>

          {/* Past periods */}
          <div className="flex items-center gap-3 mb-3">
            <span className="w-1 h-5 bg-brand-500 rounded-full" />
            <h2 className="font-heading text-[14px] tracking-[0.12em] uppercase text-ink dark:text-gray-100">Past periods</h2>
            <span className="text-gray-400 tabular text-[13px]">({submissions.length})</span>
          </div>
          <div className="space-y-2.5">
            {submissions.map((sub, i) => (
              <SubmissionCard
                key={sub.id}
                submission={sub}
                isExpanded={expandedId === sub.id}
                isActive={i === 0}
                onToggle={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
